import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom"; 
import InstructorSidebar from "../common/instructorSidebar";
import InstructorSettingsLink from "../../Instructor/instructor-settings/settings-link/instructorSettingsLink";
import {
    getMainMenu,
    createMainMenuItem,
    updateMainMenuItem,
    deleteMainMenuItem,
} from "../../../api/setting";

interface MainMenuItem {
    id?: number;
    name: string;
    link_type: "internal" | "external";
    link?: string;
    order: number;
    is_active: boolean;
}

interface MainMenu {
    id: number;
    name: string;
    type: "normal" | "dropdown";
    link_type?: "internal" | "external";
    link?: string;
    order: number;
    is_active: boolean;
    items: MainMenuItem[];
}

const SettingMainMenuItem: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const mainMenuId = Number(id);

    const [mainMenu, setMainMenu] = useState<MainMenu | null>(null);
    const [items, setItems] = useState<MainMenuItem[]>([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [newItem, setNewItem] = useState<MainMenuItem>({
        name: "",
        link_type: "internal",
        link: "",
        order: 0,
        is_active: true,
    });
    const [editingItem, setEditingItem] = useState<MainMenuItem | null>(null);

    useEffect(() => {
        const fetchMainMenu = async () => {
            if (!mainMenuId) return;
            try {
                setLoading(true);
                const data = await getMainMenu(mainMenuId);
                setMainMenu(data);
                setItems(data.items); // <-- items come directly from mainMenu
            } catch (err) {
                console.error("Failed to fetch main menu", err);
            } finally {
                setLoading(false);
            }
        };

        fetchMainMenu();
    }, [mainMenuId]);

    const handleEditItem = (item: MainMenuItem) => {
        setEditingItem(item);
        setNewItem({ ...item });
        setShowModal(true);
    };

    const handleDeleteItem = async (id?: number) => {
        if (!id) return;
        if (!window.confirm("Are you sure you want to delete this item?")) return;

        try {
            await deleteMainMenuItem(id);
            setItems(prev => prev.filter(item => item.id !== id));
        } catch (err) {
            console.error("Failed to delete item", err);
            alert("Failed to delete item");
        }
    };

    const handleChangeActive = async (id: number, value: boolean) => {
        try {
            await updateMainMenuItem(id, { is_active: value });
            setItems(prev => prev.map(item => item.id === id ? { ...item, is_active: value } : item));
        } catch (err) {
            console.error("Failed to update active status", err);
            alert("Failed to update item status");
        }
    };

    const handleSaveItem = async () => {
        if (!newItem.name.trim()) return alert("Item name is required");

        try {
            if (editingItem && editingItem.id) {
                const updated = await updateMainMenuItem(editingItem.id, newItem);
                setItems(prev => prev.map(item => item.id === editingItem.id ? updated : item));
            } else {
                const created = await createMainMenuItem({ ...newItem, main_menu_id: mainMenuId });
                setItems(prev => [...prev, created]);
            }

            setShowModal(false);
            setEditingItem(null);
            setNewItem({
                name: "",
                link_type: "internal",
                link: "",
                order: 0,
                is_active: true,
            });
        } catch (err) {
            console.error("Failed to save item", err);
            alert("Failed to save item");
        }
    };

    return (
        <div className="content">
            <div className="container">
                <div className="row">
                    <InstructorSidebar />
                    <div className="col-lg-9">
                        <div className="mb-3"><h5>Settings</h5></div>
                        <InstructorSettingsLink />

                        {loading ? (
                            <p>Loading main menu...</p>
                        ) : mainMenu ? (
                            <>
                                <div className="mb-3">
                                    <h5>Main Menu: {mainMenu.name}</h5>
                                    <p>Type: {mainMenu.type}</p>
                                </div>

                                <div className="card mb-0 mt-4">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="card-title mb-0">Sub Menu Items</h5>
                                            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ Add Item</button>
                                        </div>

                                        <table className="table align-middle">
                                            <thead>
                                                <tr>
                                                    <th style={{ background: 'none' }}>Name</th>
                                                    <th style={{ background: 'none' }}>Link Type</th>
                                                    <th style={{ background: 'none' }}>Link</th>
                                                    <th style={{ background: 'none' }}>Order</th>
                                                    <th style={{ background: 'none' }}>Active</th>
                                                    <th style={{ background: 'none' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.length === 0 ? (
                                                    <tr><td colSpan={6} className="text-center">No items found</td></tr>
                                                ) : (
                                                    items.map(item => (
                                                        <tr key={item.id}>
                                                            <td>{item.name}</td>
                                                            <td>{item.link_type}</td>
                                                            <td>{item.link || "-"}</td>
                                                            <td>{item.order}</td>
                                                            <td>
                                                                <input type="checkbox" checked={item.is_active} onChange={e => handleChangeActive(item.id!, e.target.checked)} />
                                                            </td>
                                                            <td>
                                                                <button className="btn btn-sm btn-warning me-2" onClick={() => handleEditItem(item)}>Edit</button>
                                                                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteItem(item.id)}>Delete</button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p>Main menu not found</p>
                        )}

                        {/* Modal */}
                        {showModal && (
                            <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
                                <div className="modal-dialog">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">{editingItem ? "Edit Item" : "Add New Item"}</h5>
                                            <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                        </div>
                                        <div className="modal-body">
                                            <div className="mb-3">
                                                <label className="form-label">Name</label>
                                                <input type="text" className="form-control" value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Link Type</label>
                                                <select className="form-select" value={newItem.link_type} onChange={e => setNewItem({ ...newItem, link_type: e.target.value as "internal" | "external" })}>
                                                    <option value="internal">Internal</option>
                                                    <option value="external">External</option>
                                                </select>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Link</label>
                                                <input type="text" className="form-control" value={newItem.link} onChange={e => setNewItem({ ...newItem, link: e.target.value })} />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Order</label>
                                                <input type="number" className="form-control" value={newItem.order} onChange={e => setNewItem({ ...newItem, order: Number(e.target.value) })} />
                                            </div>
                                            <div className="form-check">
                                                <input className="form-check-input" type="checkbox" checked={newItem.is_active} onChange={e => setNewItem({ ...newItem, is_active: e.target.checked })} />
                                                <label className="form-check-label">Active</label>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                            <button className="btn btn-primary" onClick={handleSaveItem}>Save Item</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingMainMenuItem;
